import { Button, Dialog } from '@hai/ui-react';
import { isNil } from 'ramda';
import React, { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormSwitch } from 'src/js/component/base/form/form-switch';
import { useModalControls } from 'src/js/hook/use-modal-controls';
import { constant } from 'src/js/util/constant';
import { scrollToId } from '../../helper/scroll-helper';
import { useMutable } from '../../hook/use-mutable';
import { createListStore, ListSelector } from '../../util/listSelector';
import { PresetDeleteModal } from './preset-delete-modal';
import { PresetEditModal } from './preset-edit-modal';
import { presetNames } from './preset-helper';
import { PresetImport } from './preset-import';
import { PresetViewModel, Presets, newPresetViewModel, Preset } from './preset-models';
import { v4 as uuidv4 } from 'uuid';
import {
  createPreset,
  duplicatePreset,
  fetchPresets,
  loadPreset,
  renamePreset,
  setAutoSavePreset,
  setStartupPreset,
  uploadPreset,
} from './preset-store';
import { PresetManagerTable } from './preset-manager-table';
import { triggerEvent } from '../../events';
import { RebootNeededNotSavedModal } from '../../reboot/reboot-needed-not-saved-modal';
import { useLocalStorage } from 'usehooks-ts';
import { notificationHandler } from '../../notification/notification-handler';
import { createAndDispatchNotification } from '../../notification/notification-helper';
import { NotificationVariant } from '../../notification/notification';
import { ButtonStateType } from '@hai/ui-react/dist/types';

const SHOW_NEW_PRESET_BUTTON = false; /* New Preset is not shown as of 28.Nov.2023 */

interface Props {
  presets: Presets;
  onCancel: VoidFunction;
  shouldReloadData: VoidFunction;
}

export const PresetManager: React.FunctionComponent<Props> = ({ presets, onCancel, shouldReloadData }) => {
  const { t } = useTranslation();
  const [singleItem, setSingleItem] = useState<Preset>(); // for kebab selection
  let listController: ListSelector<Preset> = null;
  const [uuid] = useMutable(uuidv4());
  listController = createListStore(presets.data, uuid(), 'name');
  const [savePromptModalShown, showSavePromptModal, hideSavePromptModal] = useModalControls(); // FIXME rename to upload related
  const [selectedFile, setSelectedFile] = useMutable(null);
  const sortDirectionKey = `${constant.lStoragePrefix}:presetManager:ActivateImportedPreset`;
  const [activateImportedPreset, setActivateImportedPreset] = useLocalStorage(sortDirectionKey, false);
  const [importShown, showImport, hideImport] = useModalControls();
  const [newShown, showNew, hideNew] = useModalControls();
  const [deleteShown, showDelete, hideDelete] = useModalControls();
  const [renameShown, showRename, hideRename] = useModalControls();
  const [importButtonState, setImportButtonState] = useState<ButtonStateType>(undefined);

  const loadPresetAction = (presetName: string, notification: boolean) => {
    return loadPreset({ name: presetName }, t, notification).then((res) => {
      if (res.ok) {
        shouldReloadData();
        triggerEvent('preset-load');
      }
      //FIXME : What happens if the loadPreset doesn't have a "res.ok"
      return res;
    });
  };

  //TODO this one could return a promise and then we finalize on the promise end.
  const uploadAndloadPresetAction = (file: File) => {
    setImportButtonState('pending');
    if (activateImportedPreset) {
      uploadPreset(file, t, false).then((res) => {
        if (!res.ok) {
          setImportButtonState(undefined);
          triggerEvent('enable-preset-fetch');
          createAndDispatchNotification(
            t('presets.notifications.importConfigurationErrorMsg'),
            NotificationVariant.ERROR,
          );
          return;
        }
        loadPresetAction(file.name, false).then((res) => {
          //FIXME : Make sure there error case is caught because the notification because it is set to false
          if (!presets.autosave) {
            setStartupPreset({ name: file.name }, t, false).then((res) => {
              setImportButtonState(undefined);
              triggerEvent('enable-preset-fetch');
              notificationHandler(
                res,
                t('presets.notifications.importAndLoadSuccessMsg', { name: file.name }),
                t('presets.notifications.importAndLoadErrorMsg', { name: file.name }),
                t,
              );
            });
          } else {
            setImportButtonState(undefined);
            triggerEvent('enable-preset-fetch');
            notificationHandler(
              res,
              t('presets.notifications.importAndLoadSuccessMsg', { name: file.name }),
              t('presets.notifications.importAndLoadErrorMsg', { name: file.name }),
              t,
            );
          }
        }); //comment
      });
    } else {
      uploadPreset(file, t, true).then(() => {
        setImportButtonState(undefined);
        triggerEvent('enable-preset-fetch');
        shouldReloadData();
      });
    }
  };

  const handleImportPreset = (file: File): void => {
    handleImportShown();
    triggerEvent('stop-preset-fetch');
    if (activateImportedPreset && presets.activeWasModified) {
      setSelectedFile(file);
      showSavePromptModal();
    } else {
      uploadAndloadPresetAction(file);
    }
  };

  const handleImportShown = (): void => {
    hideImport();
    shouldReloadData();
  };

  const reloadPresetData = (): void => {
    listController.unselectAll();
    shouldReloadData();
  };

  const handleSetAsStartup = (preset: PresetViewModel) => {
    setStartupPreset(preset, t, true).then((res) => {
      if (res.ok) {
        reloadPresetData();
      }
      //FIXME : What happens if the setStartupPreset doesn't have a "res.ok"
    });
  };

  const handleSetAutoSave = (checked: boolean) => {
    setAutoSavePreset(checked, t).then((res) => {
      if (res.ok) {
        reloadPresetData();
      }
      //FIXME : What happens if the setAutoSavePreset doesn't have a "res.ok"
    });
  };

  const handleNew = (preset: PresetViewModel, overwrite: boolean): void => {
    createPreset(
      {
        ...preset,
        name: `${preset.name}.cfg`,
        startup: preset.startup,
        changeActive: preset.startup,
      },
      overwrite,
      t,
    ).then((res) => {
      if (res.ok) {
        hideNew();
        fetchPresets(t).then(() => {
          setTimeout(() => {
            scrollToId(`${preset.name}.cfg`, 48);
          }, 300);
        });
      }
    });
  };

  const onSuccessDelete = (): void => {
    hideDelete();
    listController.unselectAll();
    setSingleItem(undefined);
    shouldReloadData();
    //setSelectAll(false); // FIXME, needed?
  };

  const handleRename = (preset: PresetViewModel, overwrite: boolean, originalName: string): void => {
    renamePreset({ ...preset, name: `${preset.name}.cfg` }, overwrite, originalName, t).then((res) => {
      if (res.ok) {
        hideRename();
        shouldReloadData();
        listController.unselectAll();
      }
    });
  };

  const handleDuplicate = (item: Preset): void => {
    duplicatePreset(item, t).then((res) => {
      if (res.ok) {
        shouldReloadData();
        listController.unselectAll();
      }
    });
  };
  if (isNil(presets)) {
    return null;
  }

  const content = (): ReactNode => {
    return (
      <form className="HaiForm" id="preset-manager">
        <div className="d-flex">
          <FormSwitch
            name="autosave"
            switchLabel={t('presets.autoSave')}
            checked={presets.autosave}
            onChange={handleSetAutoSave}
            className="hai-mb-2 ml-auto"
          />
        </div>
        <PresetManagerTable
          listController={listController}
          presets={presets}
          onDelete={showDelete}
          onDuplicate={handleDuplicate}
          onLoadClicked={loadPresetAction}
          onRename={showRename}
          onSetAsStartup={presets.autosave ? null : handleSetAsStartup}
          shouldReloadData={shouldReloadData}
          setSingleItem={setSingleItem}
        />
        {!importShown && (
          <div className="d-flex hai-mt-8">
            <Button state={importButtonState} onClick={showImport}>
              {importShown ? t('general.cancel') : t('general.import')}
            </Button>
            {SHOW_NEW_PRESET_BUTTON && <Button onClick={showNew}>{t('presets.modal.form.newTitle')}</Button>}
            <Button
              variant="primary"
              className="ml-auto"
              onClick={() => {
                listController.unselectAll();
                onCancel();
              }}
            >
              {t('general.done')}
            </Button>
          </div>
        )}
        {importShown && (
          <PresetImport
            activateImportedPreset={activateImportedPreset}
            autosave={presets.autosave}
            handleImportPreset={handleImportPreset}
            setActivateImportedPreset={setActivateImportedPreset}
            onCancel={hideImport}
            presetNames={presetNames(presets.data.map((p) => p.name))}
          />
        )}
        {newShown && (
          <PresetEditModal
            autosave={presets.autosave}
            showSetAsStartupCheckbox={true}
            viewModel={newPresetViewModel()}
            presetNames={presetNames(presets.data.map((p) => p.name))}
            onSubmit={handleNew}
            onCancel={hideNew}
            title={t('presets.modal.form.newTitle')}
            btnText={t('general.create')}
          />
        )}
        {renameShown && (
          <PresetEditModal
            autosave={presets.autosave}
            showSetAsStartupCheckbox={false}
            viewModel={singleItem}
            presetNames={presetNames(presets.data.map((p) => p.name))}
            onSubmit={handleRename}
            onCancel={() => {
              hideRename();
              listController.unselectAll();
            }}
            title={t('presets.modal.form.renameTitle')}
            btnText={t('general.rename')}
          />
        )}
        {deleteShown && (
          <PresetDeleteModal
            itemsToDelete={singleItem ? [singleItem] : listController.selectedRows()}
            onCancel={() => {
              hideDelete();
              setSingleItem(undefined);
            }}
            onSuccessDelete={onSuccessDelete}
          />
        )}
        {savePromptModalShown && (
          //TODO: Rename modal, find better name
          <RebootNeededNotSavedModal
            onCancel={hideSavePromptModal}
            onReboot={() => {
              //import
              hideSavePromptModal();
              uploadAndloadPresetAction(selectedFile());
            }}
            onSave={() => {
              //Save and import
              hideSavePromptModal();
              triggerEvent('stop-preset-fetch');
              triggerEvent('preset-save-callback', {
                offerSetAsStartup: false,
                callback: (_res: any) => {
                  uploadAndloadPresetAction(selectedFile()); // Import and load new preset file
                },
              });
            }}
            bodyText={
              <>
                {t('upload.presetUploadNotSavedModal.body1')}
                <br />
                {t('upload.presetUploadNotSavedModal.body2')}
              </>
            }
            middleLabel={t('upload.import')}
            lastLabel={t('upload.saveAndImport')}
            middleVariant="secondary"
            lastVariant="danger"
          />
        )}
      </form>
    );
  };

  return (
    <Dialog
      accentColor={constant.productColor}
      title={t('presets.presetManager')}
      onClose={() => {
        listController.unselectAll();
        onCancel();
      }}
      show={true}
      dialogType="activity"
      headerIcon="Presets"
      content={content()}
      autoScroll={false}
      size="lg"
      className="mk-modal-extra-large"
    />
  );
};
